/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { AxiosError, AxiosResponse } from 'axios';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { useToast } from 'hooks/toast';
import api from 'services/api';
import FormConstructor, { IInput, ISaveBtn } from 'components/FormConstructor';
import getValidationErrors from 'utils/getValidationErrors';
import { IDepartamentosDTO, ISelectDTO } from 'utils/DTOS';

import Spinner from 'components/Spinner';
import { useAuth } from 'hooks/auth';
import EdicaoHorasTecnicasModal from 'components/EdicaoHorasTecnicasModal';
import {
  Container,
  HeaderHolder,
  HeaderItem,
  BodyHolder,
  ModalOpener,
} from '../interna-styles';
import { TitleHolder } from '../styles';

interface ParamsTypes {
  id: string | undefined;
}

const ServicosInterna: React.FC = () => {
  const module = 'servico';
  const module_label = 'Serviço';

  const { id } = useParams<ParamsTypes>();

  const history = useHistory();

  const formRef = useRef<FormHandles>(null);
  const [update, setUpdate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any | undefined>(undefined);
  const [departamentos, setDepartamentos] = useState<ISelectDTO[]>([]);
  const { addToast } = useToast();
  const { cod_perfil } = useAuth().usuario;

  const getDepartamentos = useCallback(async () => {
    await api
      .get('/departamentos')
      .then(async (res: AxiosResponse) => {
        const tmp: IDepartamentosDTO[] = res.data;
        const final: ISelectDTO[] = [];

        for (let i = 0; i < tmp.length; i++) {
          const tmp1 = tmp[i];

          final.push({
            label: tmp1.nome,
            value: tmp1.id.toString(),
          });
        }

        setDepartamentos(final);
      })
      .catch((err: AxiosError) => {
        addToast({
          type: 'error',
          title:
            typeof err.response?.data.message === 'string'
              ? err.response?.data.message
              : 'Ocorreu um erro',
          description: `Ocorreu um erro ao buscar os departamentos, tente novamente.`,
        });
        console.error(`Erro: ${err}`);
      });
  }, [addToast]);

  useEffect(() => {
    getDepartamentos();
  }, [getDepartamentos]);

  const saveBtn: ISaveBtn = {
    label: 'Enviar',
    loading,
  };

  const inputs: IInput[] = [
    {
      name: 'nome',
      label: 'Nome',
      tipo: 'input',
      required: true,
    },
    {
      name: 'escopo',
      label: 'Escopo',
      tipo: 'textarea',
      required: true,
    },
    {
      name: 'departamento_id',
      label: 'Departamento',
      tipo: 'select',
      selectData: departamentos,
      required: true,
    },
    {
      name: 'recorrente',
      label: 'Recorrente',
      tipo: 'checkbox',
      required: true,
      marked: data && data.recorrente,
    },
    {
      name: 'pontos',
      label: 'Pontos',
      tipo: 'input',
      type: 'number',
      required: true,
    },
    {
      name: 'fg_ativo',
      label: 'Ativo',
      tipo: 'checkbox',
      required: true,
      marked: data && data.fg_ativo,
    },
  ];

  const get = useCallback(async () => {
    setLoading(true);
    await api
      .get(`/${module}s/${module}/${id}`)
      .then(async (res: AxiosResponse) => {
        res.data.departamento_id = res.data.__departamento__.id;
        setData(res.data);
      })
      .catch(err => {
        addToast({
          type: 'error',
          title:
            typeof err.response?.data.message === 'string'
              ? err.response?.data.message
              : 'Ocorreu um erro',
          description: `Ocorreu um erro ao buscar o ${module.toLocaleLowerCase()}, tente novamente.`,
        });
        console.error(`Erro: ${err}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [addToast, id, module, update]);

  useEffect(() => {
    get();
  }, [get]);

  const handleSubmit = useCallback(
    async (dat: any) => {
      try {
        setLoading(true);

        formRef.current?.setErrors({});

        dat.recorrente = dat.recorrente[0] === 'true';
        dat.fg_ativo = dat.fg_ativo[0] === 'true';

        const schema = Yup.object().shape({
          nome: Yup.string().required('Nome obrigatório'),
          escopo: Yup.string().required('Escopo obrigatório'),
          departamento_id: Yup.string().required('Departamento obrigatório'),
          pontos: Yup.string().required('Pontuação obrigatória'),
        });

        await schema.validate(dat, {
          abortEarly: false,
        });

        api
          .put(`/${module}s/${module}/${id}`, dat)
          .then((res: AxiosResponse) => {
            const { id: idd } = res.data;
            addToast({
              type: 'success',
              title: `${module_label}${
                idd && ` de id: ${idd}`
              } atualizado com sucesso`,
            });
            setUpdate(!update);
            history.push(`/cadastros/${module}s`);
          })
          .catch((err: AxiosError) => {
            addToast({
              type: 'error',
              title:
                typeof err.response?.data.message === 'string'
                  ? err.response?.data.message.charAt(0).toUpperCase() +
                    err.response?.data.message.slice(1)
                  : 'Ocorreu um erro',
              description: `Ocorreu um erro ao atualizar o ${module}, cheque as informações e tente novamente.`,
            });
            console.error(`Erro: ${err}`);
          });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        addToast({
          type: 'error',
          title: err.message || `Erro na atuaização do ${module}`,
          description: `Ocorreu um erro ao atualizar o ${module}, cheque as informações e tente novamente.`,
        });
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [addToast, id, update]
  );

  return (
    <Container>
      {data ? (
        <>
          <HeaderHolder style={{ gridTemplateColumns: '300px' }}>
            <HeaderItem className="full">
              <EdicaoHorasTecnicasModal
                servico={data}
                readonly={cod_perfil === 4}
                setUpdate={setUpdate}
                update={update}
              >
                <ModalOpener>Gerenciar Horas Técnicas</ModalOpener>
              </EdicaoHorasTecnicasModal>
            </HeaderItem>
          </HeaderHolder>
          <BodyHolder style={{ marginTop: 60 }}>
            <TitleHolder>
              {module_label} #{id}:
            </TitleHolder>
            <FormConstructor
              color="quaternary"
              formColumns="1fr"
              formRef={formRef}
              inputs={inputs}
              saveBtn={saveBtn}
              submitFnc={handleSubmit}
              initialData={data}
              readonly={cod_perfil === 4}
            />
          </BodyHolder>
        </>
      ) : loading ? (
        <Spinner size={15} />
      ) : (
        `${
          module.lastIndexOf('s') === module.length - 1
            ? module.slice(0, -1)
            : module
        } não encontrado...`
      )}
    </Container>
  );
};

export default ServicosInterna;
